import { Ionicons } from '@expo/vector-icons';
import { VideoView } from 'expo-video';
import { Dimensions, Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useMediaPlayer } from '../contexts/MediaPlayerContext';

const MediaPlayerModal = () => {
  const {
    isModalVisible,
    currentMedia,
    mediaType,
    error,
    audioStatus,
    videoPlayer,
    pauseMedia,
    resumeMedia,
    hideModal,
    stopMedia,
  } = useMediaPlayer();

  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (mediaType === 'audio' && audioStatus) {
      if (audioStatus.playing) {
        pauseMedia();
      } else {
        resumeMedia();
      }
    } else if (mediaType === 'video' && videoPlayer) {
      if (videoPlayer.playing) {
        pauseMedia();
      } else {
        resumeMedia();
      }
    }
  };

  const handleClose = () => {
    hideModal();
  };

  const handleStop = () => {
    stopMedia();
  };

  if (!isModalVisible || !currentMedia) {
    return null;
  }

  return (
    <Modal
      visible={isModalVisible}
      transparent={true}
      animationType="slide"
      onRequestClose={handleClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          {/* Header with close button */}
          <View style={styles.header}>
            <TouchableOpacity 
              style={styles.closeButton}
              onPress={handleClose}
            >
              <Ionicons name="close" size={24} color="#333" />
            </TouchableOpacity>
          </View>

          {/* Media content */}
          <View style={styles.mediaContent}>
            {mediaType === 'video' ? (
              <View style={styles.videoContainer}>
                <VideoView
                  player={videoPlayer}
                  style={styles.video}
                  allowsFullscreen
                  allowsPictureInPicture
                />
              </View>
            ) : (
              <View style={styles.audioContainer}>
                {/* Minimal audio container - no visual elements */}
              </View>
            )}
          </View>

          {/* Controls */}
          <View style={styles.controls}>
            {mediaType === 'audio' && audioStatus && (
              <View style={styles.audioControls}>
                {/* Progress bar for audio */}
                {audioStatus.duration > 0 && (
                  <View style={styles.progressContainer}>
                    <Text style={styles.timeText}>
                      {formatTime(audioStatus.currentTime)}
                    </Text>
                    <View style={styles.progressBar}>
                      <View 
                        style={[
                          styles.progressFill, 
                          { width: `${(audioStatus.currentTime / audioStatus.duration) * 100}%` }
                        ]} 
                      />
                    </View>
                    <Text style={styles.timeText}>
                      {formatTime(audioStatus.duration)}
                    </Text>
                  </View>
                )}
                
                {/* Play/Pause button */}
                <View style={styles.playbackControls}>
                  <TouchableOpacity 
                    style={styles.controlButton}
                    onPress={handlePlayPause}
                  >
                    <Ionicons 
                      name={audioStatus.playing ? 'pause' : 'play'} 
                      size={40} 
                      color="#fff" 
                    />
                  </TouchableOpacity>
                  
                  <TouchableOpacity 
                    style={styles.stopButton}
                    onPress={handleStop}
                  >
                    <Ionicons name="stop" size={30} color="#fff" />
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {mediaType === 'video' && (
              <View style={styles.videoControls}>
                <TouchableOpacity 
                  style={styles.controlButton}
                  onPress={handlePlayPause}
                >
                  <Ionicons 
                    name={videoPlayer?.playing ? 'pause' : 'play'} 
                    size={40} 
                    color="#fff" 
                  />
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={styles.stopButton}
                  onPress={handleStop}
                >
                  <Ionicons name="stop" size={30} color="#fff" />
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Error display */}
          {error && (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    width: screenWidth * 0.9,
    maxHeight: screenHeight * 0.8,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  title: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
    flex: 1,
    marginRight: 16,
  },
  closeButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#f0f0f0',
  },
  mediaContent: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#000',
  },
  video: {
    width: '100%',
    height: '100%',
  },
  audioContainer: {
    width: '100%',
    minHeight: 20,
  },
  controls: {
    padding: 20,
  },
  audioControls: {
    alignItems: 'center',
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    marginBottom: 20,
  },
  timeText: {
    color: '#666',
    fontSize: 12,
    minWidth: 40,
    textAlign: 'center',
  },
  progressBar: {
    flex: 1,
    height: 4,
    backgroundColor: '#e0e0e0',
    borderRadius: 2,
    marginHorizontal: 12,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#007AFF',
  },
  playbackControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
  },
  videoControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
  },
  controlButton: {
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  stopButton: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#ccc',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  errorText: {
    color: '#ff4444',
    fontSize: 14,
    textAlign: 'center',
  },
});

export default MediaPlayerModal;